/**
 * 生成加载序列组件
 * 实现四步加载动画序列
 * Requirements: 15.1, 15.2
 */

import { useEffect, useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface LoadingStep {
  id: string;
  label: string;
  duration: number; // 毫秒
}

const defaultSteps: LoadingStep[] = [
  { id: "analyze", label: "分析图片特征...", duration: 1500 },
  { id: "match", label: "匹配护理方案...", duration: 1200 },
  { id: "calculate", label: "计算个性化剂量...", duration: 1000 },
  { id: "generate", label: "生成护理时间轴...", duration: 800 },
];

interface GenerationLoadingSequenceProps {
  steps?: LoadingStep[];
  onComplete?: () => void;
  className?: string;
}

export function GenerationLoadingSequence({
  steps = defaultSteps,
  onComplete,
  className,
}: GenerationLoadingSequenceProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (currentStep >= steps.length) {
      onComplete?.();
      return;
    }

    const timer = setTimeout(() => {
      setCompletedSteps((prev) => new Set([...prev, steps[currentStep].id]));
      setCurrentStep((prev) => prev + 1);
    }, steps[currentStep].duration);

    return () => clearTimeout(timer);
  }, [currentStep, steps, onComplete]);

  return (
    <div className={cn("space-y-4", className)}>
      {steps.map((step, index) => {
        const isCompleted = completedSteps.has(step.id);
        const isCurrent = index === currentStep;
        const isPending = index > currentStep;

        return (
          <div
            key={step.id}
            className={cn(
              "flex items-center gap-3 transition-opacity duration-300",
              isPending && "opacity-40"
            )}
          >
            {/* 状态图标 */}
            <div className="flex-shrink-0 w-6 h-6">
              {isCompleted ? (
                <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                  <Check className="w-4 h-4 text-white" />
                </div>
              ) : isCurrent ? (
                <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
              ) : (
                <div className="w-6 h-6 rounded-full border-2 border-gray-300" />
              )}
            </div>

            {/* 步骤文字 */}
            <span
              className={cn(
                "text-sm",
                isCompleted && "text-green-600",
                isCurrent && "text-blue-600 font-medium",
                isPending && "text-gray-400"
              )}
            >
              {step.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default GenerationLoadingSequence;
